import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Arrow } from 'src/components/svg/Arrows';
import { Input } from 'src/components/Inputs/Input';
import promocodeIcon from 'src/assets/images/promocodeIcon.webp';
import { useScrollIntoView } from 'react-easyrocket';
import { Spin } from 'src/components/svg/General';

interface DepositPromoCodeProps {
  changePromoCode: (code: string) => void;
  label?: string;
  placeholder?: string;
  value: string;
  isModal: boolean;
  isPromocodeError: boolean;
  isLoadingCheckPromocode: boolean;
}

const _DepositPromoCode = ({
  changePromoCode,
  value,
  label,
  placeholder,
  isModal,
  isPromocodeError,
  isLoadingCheckPromocode
}: DepositPromoCodeProps) => {
  const { t } = useTranslation();
  const [showPromocode, setShowPromocode] = useState(false);

  const handleChangePromoCode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      changePromoCode(event.target.value);
    },
    [changePromoCode]
  );

  useEffect(() => {
    if (value && !showPromocode) {
      setShowPromocode(true);
    }
  }, [value]);

  const changeShowPromocode = () => {
    setShowPromocode((showPromocode) => !showPromocode);
  };

  const promocodeInputRef = useScrollIntoView({
    condition: showPromocode && isModal,
    effectDependencies: [showPromocode, isModal]
  });

  return (
    <div className="flex flex-col justify-between gap-5 mb-5 mt-2.5">
      <div
        className="flex gap-3 items-center cursor-pointer select-none"
        onClick={changeShowPromocode}>
        <img src={promocodeIcon} alt="promocode icon" className="h-6 w-6" />
        <p className="font-bold text-sm text-wdap_title">{t('promoCode.title')}</p>
        <Arrow
          fill="var(--b_secondary_text)"
          className={classNames('w-[20px] stroke-wdap_arrow', showPromocode ? 'rotate-180' : '')}
        />
      </div>
      {showPromocode ? (
        <div className="flex flex-col gap-2">
          {label ? <p className="ml-[15px]">{label}</p> : null}
          <div className="relative" ref={promocodeInputRef}>
            <Input
              className="text-[16px] font-bold !h-[30px] uppercase"
              placeholder={placeholder}
              value={value}
              onChange={handleChangePromoCode}
              rightIcon={isLoadingCheckPromocode && <Spin />}
            />
          </div>
          {isPromocodeError && !isLoadingCheckPromocode && (
            <p className="text-wdap_error text-xs font-medium">
              {t('promoCode.incorrectPromocode')}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export const DepositPromoCode = memo(_DepositPromoCode);
